import React, { useState, useEffect, useRef } from "react";
import { Card, Form, Row, Col } from "react-bootstrap";
import ErrorComponent from "../../../Components/ErrorMessage";
import "./MultiChoiceQuestion.css";
import { replacePlaceholder } from "../../../Utils/Utils";
function MultiChoiceQuestion({
  question,
  handleChange,
  formValues,
  showError,
  getChoiceOrder,
  setChoiceOrder,
}) {
  const [sortedChoices, setSortedChoices] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [otherTextMap, setOtherTextMap] = useState({});

  const questionKey = question.tempId;
  const selectedValues = formValues[questionKey] || [];
  const hasAnswer = selectedValues.length > 0;

  /* ---------- RANDOMIZE refs (same as RadioGroup) ---------- */
  const seedRef = useRef({});

  const shuffleArrayWithSeed = (array, seed) => {
    const newArray = [...array];
    let random = seed;
    for (let i = newArray.length - 1; i > 0; i--) {
      random = (random * 9301 + 49297) % 233280;
      const j = Math.floor((random / 233280) * (i + 1));
      [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
    }
    return newArray;
  };

  /* ---------- EXISTING logic (unchanged) ---------- */
  useEffect(() => {
    const otherTexts = {};
    for (const val of selectedValues) {
      if (val.startsWith("Other:")) {
        const parts = val.split(":");
        if (parts.length === 3) {
          otherTexts[parts[1]] = parts[2];
        }
      }
    }

    setOtherTextMap(prev => {
      const updated = { ...prev };
      let changed = false;
      for (const key in otherTexts) {
        if (updated[key] !== otherTexts[key]) {
          updated[key] = otherTexts[key];
          changed = true;
        }
      }
      return changed ? updated : prev;
    });
  }, [JSON.stringify(selectedValues)]);

  /* ---------- RANDOMIZE logic (updated only here) ---------- */
  useEffect(() => {
    // use frozen order if exists
    const frozen = getChoiceOrder?.(questionKey);
    if (frozen) {
      setSortedChoices(frozen);
      return;
    }

    let choices = [...question.choices];
    const noneOption = choices.find(c => c.value === "None");
    choices = choices.filter(c => c.value !== "None");

    if (question.choicesOrder === "random") {
      if (!seedRef.current[questionKey]) {
        seedRef.current[questionKey] = Math.random();
      }
      choices = shuffleArrayWithSeed(
        choices,
        seedRef.current[questionKey]
      );
    } else if (question.choicesOrder === "ascending") {
      choices.sort((a, b) => a.text.localeCompare(b.text));
    } else if (question.choicesOrder === "descending") {
      choices.sort((a, b) => b.text.localeCompare(a.text));
    }

    if (noneOption) choices.push(noneOption);
    setSortedChoices(choices);
  }, [questionKey, question.choices, question.choicesOrder]);

  /* ---------- FREEZE on first interaction ---------- */
  const handleOptionChange = (choice) => {
    const frozen = getChoiceOrder?.(questionKey);
    if (!frozen && setChoiceOrder) {
      setChoiceOrder(questionKey, sortedChoices);
    }

    let newValues;
    const isOther = choice.optionType === "OTHER";

    if (choice.value === "None") {
      newValues = selectedValues.includes("None") ? [] : ["None"];
    } else {
      const baseValues = selectedValues.filter(v => {
        if (isOther) return !v.startsWith(`Other:${choice.tempId}:`);
        return v !== "None" && v !== choice.value;
      });

      const selected = isOther
        ? selectedValues.some(v => v.startsWith(`Other:${choice.tempId}:`))
        : selectedValues.includes(choice.value);

      if (selected) {
        newValues = baseValues;
      } else {
        newValues = isOther
          ? [...baseValues, `Other:${choice.tempId}:${otherTextMap[choice.tempId] || ""}`]
          : [...baseValues, choice.value];
      }
    }

    handleChange({
      target: {
        name: questionKey,
        value: newValues,
        type: "checkbox",
      },
    });
  };

  /* ---------- EXISTING logic (unchanged) ---------- */
  const handleOtherTextChange = (tempId, text) => {
    setOtherTextMap(prev => ({ ...prev, [tempId]: text }));
    const baseValues = selectedValues.filter(v => !v.startsWith(`Other:${tempId}:`));
    const newValues = text
      ? [...baseValues, `Other:${tempId}:${text}`]
      : baseValues;

    handleChange({
      target: {
        name: questionKey,
        value: newValues,
        type: "checkbox",
      },
    });
  };

  /* ---------- UI (UNCHANGED) ---------- */
  return (
    <Card className={`mt-3 ${question.isMandatory && showError && !hasAnswer ? "border-danger" : ""}`}>
      <Card.Body>
        <Form.Group>
          <div className="d-flex flex-column">
            <Form.Label>
              <b style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                {questionKey}. {replacePlaceholder(question.questionText, formValues)}
              </b>
              {question.isMandatory && <span className="text-danger">*</span>}
            </Form.Label>

            <Form.Label>
              <b style={{ color: "lightblue" }}>{question.questionDescription}</b>
            </Form.Label>

            {question.isMandatory && showError && !hasAnswer && (
              <ErrorComponent message={question.requiredErrorText} />
            )}
          </div>

          <Row style={{ padding: "10px" }}>
            {sortedChoices.map(choice => {
              const isOther = choice.optionType === "OTHER";
              const isChecked = isOther
                ? selectedValues.some(v => v.startsWith(`Other:${choice.tempId}:`))
                : selectedValues.includes(choice.value);

              const otherText = otherTextMap[choice.tempId] || "";
              const showInput = isChecked && isOther;

              return (
                <React.Fragment key={choice.tempId}>
                  <Row className="mb-1">
                    <Col xs={12} className="checkbox-container">
                      <Form.Check
                        type="checkbox"
                        id={`${questionKey}-${choice.value}-${choice.tempId}`}
                        label={choice.text}
                        checked={isChecked}
                        onChange={() => handleOptionChange(choice)}
                        disabled={
                          (choice.value !== "None" && selectedValues.includes("None")) ||
                          (choice.value === "None" &&
                            selectedValues.length > 0 &&
                            !selectedValues.includes("None"))
                        }
                      />
                    </Col>
                  </Row>

                  {showInput && (
                    <Row className="mb-3">
                      <Col xs={12} style={{ paddingLeft: 0, paddingRight: 0 }}>
                        <Form.Control
                          type="text"
                          placeholder="Type here..."
                          value={otherText}
                          onChange={(e) =>
                            handleOtherTextChange(choice.tempId, e.target.value)
                          }
                        />
                      </Col>
                    </Row>
                  )}
                </React.Fragment>
              );
            })}
          </Row>
        </Form.Group>
      </Card.Body>
    </Card>
  );
}

export default MultiChoiceQuestion;
